import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, LogOut, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds/waitlist";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
const Stat = ({ label, value, testid }) => (
  <div className="border border-border bg-white rounded-lg px-6 py-5">
    <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{label}</p>
    <p className="font-display text-5xl mt-2" data-testid={testid}>{value}</p>
  </div>
);

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [data, setData] = useState({ total: 0, last_7_days: 0, matching: 0, page: 1, pages: 1, items: [] });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async (query, pg) => {
    try {
      const { data } = await api.get("/admin/signups", { params: { page: pg, ...(query ? { q: query } : {}) } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q, page), 250);
    return () => clearTimeout(t);
  }, [q, page, load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/signups/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `archana-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Spreadsheet downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/signups/${toDelete.id}`);
      toast.success(`Removed ${toDelete.name}`);
      setToDelete(null);
      load(q, page);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-muted" data-testid={ADMIN.dashboard}>
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="font-display text-xl">Archana <span className="italic text-primary">Relationship</span> School <span className="text-muted-foreground text-base not-italic">· Waitlist</span></span>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-muted-foreground">{admin?.email}</span>
            <Button variant="outline" size="sm" onClick={logout} data-testid={ADMIN.logoutButton}><LogOut className="h-4 w-4 mr-1" />Log out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Total signups" value={data.total} testid={ADMIN.totalCount} />
          <Stat label="Last 7 days" value={data.last_7_days} testid={ADMIN.weekCount} />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, email or number" className="pl-9 h-11 bg-white" data-testid={ADMIN.searchInput} />
          </div>
          <Button onClick={exportCsv} className="btn-lift h-11 bg-primary hover:bg-primary/90" data-testid={ADMIN.exportButton}>
            <Download className="h-4 w-4 mr-2" />Download spreadsheet (CSV)
          </Button>
        </div>

        <div className="bg-white border border-border rounded-lg overflow-x-auto">
          {data.items.length === 0 ? (
            <p className="p-12 text-center text-muted-foreground" data-testid={ADMIN.emptyState}>
              {q ? "No signups match your search." : "No signups yet. Share your waitlist page to get started."}
            </p>
          ) : (
            <Table data-testid={ADMIN.table}>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>WhatsApp</TableHead>
                  <TableHead>Signed up</TableHead><TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((s) => (
                  <TableRow key={s.id} data-testid={`${ADMIN.row}-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{s.email || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>
                      {s.whatsapp ? (
                        <a href={`https://wa.me/${s.country_code.slice(1)}${s.whatsapp}`} target="_blank" rel="noopener noreferrer"
                          className="hover:text-primary underline-offset-4 hover:underline whitespace-nowrap" data-testid={`admin-whatsapp-link-${s.id}`}>
                          {s.country_code} {s.whatsapp}
                        </a>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">{fmt(s.created_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => setToDelete(s)} aria-label={`Delete ${s.name}`} data-testid={`${ADMIN.deleteButton}-${s.id}`}>
                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {data.matching > 0 && (
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between text-sm text-muted-foreground">
            <span data-testid="admin-pagination-info">
              Showing {(data.page - 1) * 50 + 1}–{Math.min(data.page * 50, data.matching)} of {data.matching.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-prev-page-button">
                <ChevronLeft className="h-4 w-4" />Previous
              </Button>
              <span data-testid="admin-page-indicator">Page {data.page} of {data.pages}</span>
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="admin-next-page-button">
                Next<ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this signup?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.name} ({toDelete?.email || `${toDelete?.country_code} ${toDelete?.whatsapp}`}) will be removed from the waitlist. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.cancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90" data-testid={ADMIN.confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
